import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const GOOGLE_CLIENT_ID = '717642088150-k7ldoe9hpm34cctmmavoa8ete9bk3ddn.apps.googleusercontent.com';
const API_BASE = 'https://potluckpal-production.up.railway.app/api/auth';

const STEPS = ['info', 'auth', 'confirm', 'result'];

const DeleteAccount = () => {
  const [step, setStep] = useState('info');
  const [sessionToken, setSessionToken] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  // Load Apple Sign-In JS SDK
  useEffect(() => {
    if (document.getElementById('apple-signin-sdk')) return;
    const script = document.createElement('script');
    script.id = 'apple-signin-sdk';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const decodeJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError(null);
    try {
      const decoded = decodeJwt(credentialResponse.credential);
      const res = await fetch(`${API_BASE}/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_token: credentialResponse.credential,
          email: decoded?.email,
          name: decoded?.name,
          picture: decoded?.picture,
        }),
      });
      if (!res.ok) throw new Error('Authentication failed. Please try again.');
      const data = await res.json();
      setSessionToken(data.session_token);
      setUserInfo({ email: decoded?.email, name: decoded?.name, picture: decoded?.picture, provider: 'Google' });
      setStep('confirm');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!window.AppleID) throw new Error('Apple Sign-In is not available. Please try again.');
      window.AppleID.auth.init({
        clientId: 'com.jayanthputta.potluckpal.web',
        scope: 'name email',
        redirectURI: window.location.origin + '/delete-account',
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const identityToken = response.authorization.id_token;
      const decoded = decodeJwt(identityToken);
      const name = response.user
        ? `${response.user.name?.firstName || ''} ${response.user.name?.lastName || ''}`.trim()
        : decoded?.email || 'Apple User';

      const res = await fetch(`${API_BASE}/apple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identity_token: identityToken,
          email: decoded?.email || response.user?.email,
          name: name,
        }),
      });
      if (!res.ok) throw new Error('Authentication failed. Please try again.');
      const data = await res.json();
      setSessionToken(data.session_token);
      setUserInfo({ email: decoded?.email, name: name, provider: 'Apple' });
      setStep('confirm');
    } catch (err) {
      if (err.error === 'popup_closed_by_user') {
        setError(null);
      } else {
        setError(err.message || 'Apple Sign-In failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Account deletion failed. Please try again.');
      }
      setDeleted(true);
      setStep('result');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentStepIndex = STEPS.indexOf(step);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="privacy-container">
        <div className="grain-overlay"></div>

        <div className="privacy-content">
          <Link to="/" className="privacy-back-link">&larr; Back to Home</Link>

          <h1 className="privacy-title">Delete Account</h1>

          <div className="privacy-text">
            {/* Step Indicator */}
            <div className="delete-steps">
              {STEPS.map((s, i) => (
                <div key={s} className={`delete-step-dot ${i <= currentStepIndex ? 'delete-step-active' : ''}`}>
                  {i + 1}
                </div>
              ))}
            </div>

            {error && (
              <div className="delete-error">
                {error}
              </div>
            )}

            {/* Step 1: Info */}
            {step === 'info' && (
              <section className="privacy-section">
                <h2>What happens when you delete your account</h2>
                <p>Deleting your account is permanent and cannot be undone. Here is what will happen:</p>
                <ul>
                  <li>Your account record and personal identifiers (such as your email) will be deleted.</li>
                  <li>Your AI chat history will be deleted or de-identified.</li>
                  <li>Your identity in shared event history will be anonymized (e.g., your name replaced with "Deleted user") so other participants' event records remain intact.</li>
                  <li>Dish names, categories, and descriptions you contributed will remain for the integrity of event history, but will no longer be associated with your identity.</li>
                  <li>You will be removed from all active events.</li>
                  <li>This action cannot be reversed. You will need to create a new account if you wish to use Eranos again.</li>
                </ul>
                <button className="delete-btn delete-btn-primary" onClick={() => setStep('auth')}>
                  I understand, continue
                </button>
              </section>
            )}

            {/* Step 2: Auth */}
            {step === 'auth' && (
              <section className="privacy-section">
                <h2>Verify your identity</h2>
                <p>
                  Sign in with the same provider you used to create your account. This verifies you are the account owner before deletion.
                </p>
                <div className="delete-auth-buttons">
                  <div className="delete-auth-google">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setError('Google Sign-In failed. Please try again.')}
                      text="signin_with"
                      shape="rectangular"
                      size="large"
                      width="300"
                    />
                  </div>
                  <button
                    className="delete-btn delete-btn-apple"
                    onClick={handleAppleSignIn}
                    disabled={loading}
                  >
                     Sign in with Apple
                  </button>
                </div>
                {loading && <p className="delete-loading">Authenticating...</p>}
                <button className="delete-btn delete-btn-secondary" onClick={() => { setStep('info'); setError(null); }}>
                  &larr; Go back
                </button>
              </section>
            )}

            {/* Step 3: Confirm */}
            {step === 'confirm' && (
              <section className="privacy-section">
                <h2>Confirm account deletion</h2>
                {userInfo && (
                  <div className="delete-user-info">
                    {userInfo.picture && <img src={userInfo.picture} alt="" className="delete-user-avatar" />}
                    <div>
                      <p><strong>{userInfo.name}</strong></p>
                      <p>{userInfo.email}</p>
                      <p className="delete-user-provider">Signed in with {userInfo.provider}</p>
                    </div>
                  </div>
                )}
                <p className="delete-warning">
                  This action is permanent and cannot be undone. All your data will be deleted as described above.
                </p>
                <p>
                  Type <strong>DELETE</strong> below to confirm:
                </p>
                <input
                  type="text"
                  className="delete-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                />
                <div className="delete-confirm-actions">
                  <button
                    className="delete-btn delete-btn-danger"
                    onClick={handleDelete}
                    disabled={confirmText !== 'DELETE' || loading}
                  >
                    {loading ? 'Deleting...' : 'Permanently delete my account'}
                  </button>
                  <button className="delete-btn delete-btn-secondary" onClick={() => { setStep('auth'); setError(null); setConfirmText(''); }}>
                    Cancel
                  </button>
                </div>
              </section>
            )}

            {/* Step 4: Result */}
            {step === 'result' && deleted && (
              <section className="privacy-section">
                <h2>Account deleted</h2>
                <p>
                  Your account has been successfully deleted. All associated data has been removed or anonymized as described in our <Link to="/privacy" className="privacy-email-link">Privacy Policy</Link>.
                </p>
                <p>
                  If you have any questions, contact us at <a href="mailto:geteranos@gmail.com" className="privacy-email-link">geteranos@gmail.com</a>.
                </p>
                <Link to="/" className="delete-btn delete-btn-primary" style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }}>
                  Return to Home
                </Link>
              </section>
            )}
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default DeleteAccount;
